/**
 * HeroCarouselOneNorthDemoNotReadyForProd.tsx
 *
 * Hero Carousel component with OneNorth demo personalization integration.
 * IMPORTANT: This component is not production-ready and is intended for demo purposes only.
 */

import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import HeroCarouselItem, {
  HeroCarouselItemProps,
} from 'components/helpers/CarouselItem/HeroCarouselItem';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import { pushCarouselClickDataLayerObject } from 'lib/util/gtm-datalayer';
import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { classnames } from 'tailwindcss-classnames';
import { useEngage } from 'components/globalContext/EngageProvider';
import { logWarning } from 'lib/util/errors';

export type HeroCarouselProps = Sitecore.Override.ComponentBase &
  Components.Heroes.HeroCarousel.Fields.HeroCarousel;

const HeroCarouselOneNorthDemoNotReadyForProd = ({ fields }: HeroCarouselProps): JSX.Element => {
  const engage = useEngage();

  // Keep mutable copy so we can replace the first slide after fetch
  const [carouselItems, setCarouselItems] = useState<HeroCarouselItemProps[]>(
    () => fields?.carouselItems as unknown as HeroCarouselItemProps[]
  );

  useEffect(() => {
    if (!engage) return;

    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res: any = await engage.personalize({
          friendlyId: 'hero_carousel_first_slide_experiment',
          channel: 'WEB',
          currency: 'USD',
          language: 'EN',
          pointOfSale: process.env.CDP_POINT_OF_SALE || 'BeConnected-local',
        });

        const payload = res?.content?.json ?? res?.content ?? res;

        const copy = payload?.heroCarouselCopy;
        if (!copy) return;

        // Update slide 0
        setCarouselItems((prev) => {
          if (!prev.length) return prev;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const first = { ...prev[0], fields: { ...prev[0].fields } } as any;

          first.fields.headingText = { value: copy.headline };
          first.fields.description = { value: copy.copy };

          const FALLBACK_HERO = '/static/personalized/default_image.png';
          first.imageUrlOverride = copy.imageUrl || FALLBACK_HERO;

          first.fields.cta1 = {
            value: { ...(first.fields.cta1?.value ?? {}), text: copy.ctaText },
          };

          return [first, ...prev.slice(1)];
        });
      } catch (e) {
        logWarning('Personalize fetch failed', e);
      }
    })();
  }, [engage]);

  let clickAction = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRealIndexChange = async (swiper: any) => {
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((r) => setTimeout(r, 200));
    pushCarouselClickDataLayerObject(swiper.realIndex, clickAction, 'hero carousel', carouselItems);
    clickAction = '';
  };
  const handlePrev = () => {
    clickAction = 'carousel - left click';
  };
  const handleNext = () => {
    clickAction = 'carousel - right click';
  };

  if (!fields) {
    return <MissingDataSource />;
  }

  return (
    <section
      data-component="authorable/Heroes/heroCarousel"
      className="hero-carousel"
      data-experiment="hero-carousel"
    >
      <div className={classnames('my-10', 'md:my-16')}>
        <Swiper
          loop={carouselItems.length > 1}
          slidesPerView={1}
          spaceBetween={0}
          navigation={{
            prevEl: '.swiper-button-prev',
            nextEl: '.swiper-button-next',
          }}
          pagination={{
            clickable: true,
            el: '.swiper-pagination',
            bulletElement: 'button',
          }}
          onRealIndexChange={handleRealIndexChange}
        >
          {carouselItems.map((carouselItem, index) => (
            <SwiperSlide key={carouselItem.id ?? `${index}`}>
              <HeroCarouselItem
                {...carouselItem}
                index={index}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                carouselName={(carouselItem as any).name ?? (carouselItem as any).title}
              />
            </SwiperSlide>
          ))}
          {carouselItems.length > 1 && (
            <div slot="container-end" className={classnames('mt-10')}>
              <div className={classnames('m-auto', 'flex', 'h-6', 'w-max', 'items-center')}>
                <button type="button" className="swiper-button-prev" onClick={handlePrev}>
                  prev
                </button>
                <div className="swiper-pagination" />
                <button type="button" className="swiper-button-next" onClick={handleNext}>
                  next
                </button>
              </div>
            </div>
          )}
        </Swiper>
      </div>
    </section>
  );
};

export default HeroCarouselOneNorthDemoNotReadyForProd;
